// src/pages/ContentCalendarPage.js
//
// Month-view calendar of contentTasks. Lives at /social/calendar.
//
// Status -> color mapping (kept consistent with SocialDashboardPage.js /
// SocialReportsPage.js):
//   New Request               #9ca3af
//   Content Writing           #60a5fa
//   Content Ready             #3b82f6
//   Design / Editing          #a78bfa
//   Design Ready              #8b5cf6
//   Internal Review           #f59e0b
//   Changes Requested         #f97316
//   Ready for Client Approval #fbbf24
//   Client Approval           #eab308
//   Approved                  #14b8a6
//   Scheduled                 #6366f1
//   Published                 #22c55e
//   Cancelled                 #6b7280
//
// Each contentTask becomes one calendar event. The event's date is taken
// from (in priority order): publishedDate, else scheduledDate, else dueDate.
// Tasks with none of those three dates are excluded from the calendar.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, orderBy, query, Timestamp } from 'firebase/firestore';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { db } from '../firebase';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { getStatusLabel, getPlatformLabel } from '../utils/socialEnumLabels';
import styles from '../styles/ContentCalendarPage.module.css';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const TRANSLATIONS = {
  en: {
    pageTitle: 'Content Calendar',
    subtitle: 'See all scheduled and published content at a glance.',
    client: 'Client:',
    allClients: 'All Clients',
    platform: 'Platform:',
    allPlatforms: 'All Platforms',
    loading: 'Loading calendar...',
    untitled: 'Untitled',
  },
  ar: {
    pageTitle: 'تقويم المحتوى',
    subtitle: 'شاهد كل المحتوى المجدوَل والمنشور في لمحة.',
    client: 'العميل:',
    allClients: 'جميع العملاء',
    platform: 'المنصة:',
    allPlatforms: 'جميع المنصات',
    loading: 'جارٍ تحميل التقويم...',
    untitled: 'بدون عنوان',
  },
};

// react-big-calendar toolbar/messages localization.
const CALENDAR_MESSAGES = {
  en: {
    today: 'Today',
    previous: 'Back',
    next: 'Next',
    month: 'Month',
    week: 'Week',
    day: 'Day',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Time',
    event: 'Event',
    noEventsInRange: 'There are no events in this range.',
    showMore: (total) => `+${total} more`,
  },
  ar: {
    today: 'اليوم',
    previous: 'السابق',
    next: 'التالي',
    month: 'شهر',
    week: 'أسبوع',
    day: 'يوم',
    agenda: 'جدول الأعمال',
    date: 'التاريخ',
    time: 'الوقت',
    event: 'حدث',
    noEventsInRange: 'لا توجد أحداث في هذا النطاق.',
    showMore: (total) => `+${total} أخرى`,
  },
};

const STATUS_COLORS = {
  'New Request': '#9ca3af',
  'Content Writing': '#60a5fa',
  'Content Ready': '#3b82f6',
  'Design / Editing': '#a78bfa',
  'Design Ready': '#8b5cf6',
  'Internal Review': '#f59e0b',
  'Changes Requested': '#f97316',
  'Ready for Client Approval': '#fbbf24',
  'Client Approval': '#eab308',
  Approved: '#14b8a6',
  Scheduled: '#6366f1',
  Published: '#22c55e',
  Cancelled: '#6b7280',
};

const PLATFORMS = ['Facebook', 'Instagram', 'TikTok', 'LinkedIn', 'X', 'YouTube', 'WhatsApp Channel'];

const parseTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

const ContentCalendarPage = () => {
  const navigate = useNavigate();
  const { language, toggleLanguage, isRTL } = useSocialLanguage();
  const t = TRANSLATIONS[language];

  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [clientFilter, setClientFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');

  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('month');

  // One-time fetch of clients for the filter dropdown.
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsCol = collection(db, 'clients');
        const q = query(clientsCol, orderBy('name', 'asc'));
        const snapshot = await getDocs(q);
        setClients(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };
    fetchClients();
  }, []);

  // Fetch contentTasks; refetch whenever filters change (simple one-time getDocs approach).
  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      setError('');
      try {
        const tasksCol = collection(db, 'contentTasks');
        const snapshot = await getDocs(tasksCol);
        const fetched = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            dueDate: parseTimestamp(data.dueDate),
            scheduledDate: parseTimestamp(data.scheduledDate),
            publishedDate: parseTimestamp(data.publishedDate),
          };
        });

        const filtered = fetched.filter((task) => {
          if (clientFilter !== 'all' && task.clientId !== clientFilter) return false;
          if (platformFilter !== 'all' && !(task.platforms || [task.platform]).includes(platformFilter)) return false;
          return true;
        });

        setTasks(filtered);
      } catch (err) {
        console.error('Error fetching content tasks:', err);
        setError('Failed to load content tasks.');
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, [clientFilter, platformFilter]);

  const events = useMemo(() => {
    return tasks
      .map((task) => {
        const eventDate = task.publishedDate || task.scheduledDate || task.dueDate;
        if (!eventDate) return null;
        return {
          id: task.id,
          title: task.title || t.untitled,
          start: eventDate,
          end: eventDate,
          allDay: true,
          status: task.status,
          clientName: task.clientName,
          platform: task.platform,
        };
      })
      .filter(Boolean);
  }, [tasks, t.untitled]);

  const eventStyleGetter = useCallback((event) => {
    const color = STATUS_COLORS[event.status] || '#9ca3af';
    return {
      style: {
        backgroundColor: color,
        borderColor: color,
        color: '#fff',
      },
    };
  }, []);

  const handleSelectEvent = useCallback(
    (event) => {
      navigate(`/social/tasks/${event.id}`);
    },
    [navigate]
  );

  const handleNavigate = useCallback((newDate, view) => {
    setCalendarDate(newDate);
    if (view && view !== calendarView) setCalendarView(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarView]);

  const handleView = useCallback((newView) => {
    setCalendarView(newView);
  }, []);

  const eventComponent = useCallback(
    ({ event }) => (
      <span>
        <strong>{event.title}</strong>{' '}
        <span className={styles.eventStatusBadge}>{getStatusLabel(event.status, language)}</span>
      </span>
    ),
    [language]
  );

  return (
    <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.pageTitle}>{t.pageTitle}</h2>
          <p className={styles.subtitle}>{t.subtitle}</p>
        </div>
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      <div className={styles.filtersContainer}>
        <div className={styles.filterGroup}>
          <label htmlFor="client-filter">{t.client}</label>
          <select
            id="client-filter"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">{t.allClients}</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="platform-filter">{t.platform}</label>
          <select
            id="platform-filter"
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">{t.allPlatforms}</option>
            {PLATFORMS.map((platform) => (
              <option key={platform} value={platform}>
                {getPlatformLabel(platform, language)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <p className={styles.errorMessage}>{error}</p>}

      <div className={styles.legend}>
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <span key={status} className={styles.legendItem}>
            <span className={styles.legendDot} style={{ backgroundColor: color }} />
            {getStatusLabel(status, language)}
          </span>
        ))}
      </div>

      <div className={styles.calendarContainer}>
        {loading ? (
          <div className={styles.loadingIndicator}>{t.loading}</div>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            date={calendarDate}
            view={calendarView}
            onNavigate={handleNavigate}
            onView={handleView}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            allDayAccessor="allDay"
            style={{ height: 650 }}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'agenda']}
            components={{ event: eventComponent }}
            messages={CALENDAR_MESSAGES[language]}
            rtl={isRTL}
            popup
          />
        )}
      </div>
    </div>
  );
};

export default ContentCalendarPage;
